import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import styled from 'styled-components';
import { sub, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  Grid,
  Table as MuiTable,
  Divider as MuiDivider,
  TableHead as MuiTableHead,
  Box,
} from '@mui/material';

import { selectSelectedTargetService, selectTeamHash, selectPendingEntitiesData } from 'selectors';
import {
  REPORTS_OVERALL_INTEGRATION_DETAILS_PATH,
  allPendingMappingStatusHash,
  filterIds,
} from '@constants';
import Breadcrumbs from 'components/Breadcrumbs';
import useUrl from 'hooks/useUrl';
import { fetchPendingEntities } from 'thunk';
import { getIntegrationDetailsDeepLinkUrl, getReportTeamsPageDeepLinkUrl } from 'utils';
import { setFilters } from 'actionCreators';
import { Item, PendingEntitiesInstance } from 'types';
import { spacing } from '@mui/system';
import useAppNavigation from 'hooks/useAppNavigation';
import useSimpleMuiTableBuilder from 'hooks/useSimpleMuiTableBuilder';
import StandardPageHeader from 'components/StandardPageHeader';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { getTableConfigs, StateIds } from './tableConfigs';
import useSingleEntityModal from 'hooks/useSingleEntityModal';
import orderBy from 'lodash/orderBy';
import usePendingEntityStatusModal from 'hooks/usePendingEntityStatusModal';

const Divider = styled(MuiDivider)(spacing as any);

const Header = styled.div`
  margin-bottom: 1rem;
  text-decoration: underline;
`;

const defaultBreadCrumbItems = [
  {
    text: 'Teams',
    to: getReportTeamsPageDeepLinkUrl({}),
  },
];

const PendingEntityTableContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  .date-picker {
    margin: 0 2rem;
    .MuiInputBase-root {
      padding-top: 5px;
    }
    .MuiInputBase-input {
      padding-bottom: 17px;
    }
  }
`;

const defaultStartDay = format(toZonedTime(sub(new Date(), { days: 30 }), 'UTC'), 'yyyy-MM-dd');

const PendingEntity = () => {
  const selectedTargetService = useAppSelector(selectSelectedTargetService);
  const teamHash = useAppSelector(selectTeamHash);
  const { targetServiceId, teamId } = useUrl();
  const selectedTeam = teamHash[teamId];
  const dispatch = useAppDispatch();
  const { navigateToTeamsTable } = useAppNavigation();
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { SingleEntityModal, openModal: onSingleEntityModalOpen } = useSingleEntityModal();
  const {
    data = [],
    filter,
    isRequesting,
  } = selectLocalData<PendingEntitiesInstance[], PendingEntitiesInstance[]>({
    stateId: StateIds.PendingEntityTable,
    filterId: filterIds.pendingEntities,
    formatData: ({ stateData, filter }) => {
      return orderBy(stateData, filter.orderBy, filter.orderDirection);
    },
  });
  const { openPendingEntityStatusModal: onPendingEntityStatusModalOpen } =
    usePendingEntityStatusModal();

  const breadCrumbsItems = useMemo(() => {
    if (selectedTeam && selectedTargetService && +selectedTargetService.id === +targetServiceId) {
      const targetServiceId = selectedTargetService.id.toString();
      return [
        ...defaultBreadCrumbItems,
        {
          text: `${selectedTeam.name}'s integration`,
          to: getIntegrationDetailsDeepLinkUrl(
            selectedTeam.id,
            selectedTargetService.name,
            targetServiceId,
          ),
        },
        {
          text: 'Pending entities',
          to: '',
        },
      ];
    }
    return [
      ...defaultBreadCrumbItems,
      {
        text: 'Pending entities',
        to: '',
      },
    ];
  }, [selectedTargetService, selectedTeam, targetServiceId]);

  useEffect(() => {
    if (
      +selectedTargetService?.id === +targetServiceId &&
      selectedTargetService?.pendingEntitiesDataTypes &&
      selectedTargetService?.pendingEntitiesMappingStatuses
    ) {
      dispatchLocal(fetchPendingEntities, {
        params: {
          targetServiceId: +targetServiceId,
          dataTypes: selectedTargetService.pendingEntitiesDataTypes,
          mappingStatuses: selectedTargetService.pendingEntitiesMappingStatuses,
          day: defaultStartDay,
        },
        stateId: StateIds.PendingEntityTable,
      });
      dispatch(
        setFilters({
          filterId: filterIds.pendingEntities,
          page: 0,
          rowsPerPage: 50,
          orderBy: 'id',
          orderDirection: 'asc',
        }),
      );
    } else {
      navigateToTeamsTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, targetServiceId, selectedTargetService]);

  /** Table Builder */

  // const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  // const onCollapse = (id: number) => () => {
  //   setExpandedRows({
  //     ...expandedRows,
  //     [id]: !expandedRows[id],
  //   });
  // };

  const [day, setDay] = useState<string>(defaultStartDay);
  const [selectedMappingStatuses, setSelectedMappingStatuses] = useState<string[]>(
    selectedTargetService?.pendingEntitiesMappingStatuses || [],
  );

  const onDayChange = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      const selectedDay = event.target.value;
      if (day !== selectedDay) {
        dispatchLocal(fetchPendingEntities, {
          params: {
            targetServiceId: +targetServiceId,
            dataTypes: selectedTargetService?.pendingEntitiesDataTypes as string[],
            mappingStatuses: selectedMappingStatuses,
            day: event.target.value,
          },
          stateId: StateIds.PendingEntityTable,
        });

        dispatch(
          setFilters({
            filterId: filterIds.pendingEntities,
            page: 0,
            rowsPerPage: 50,
            orderBy: 'id',
            orderDirection: 'asc',
          }),
        );
        setDay(selectedDay);
      }
    },
    [
      day,
      dispatch,
      dispatchLocal,
      selectedMappingStatuses,
      selectedTargetService?.pendingEntitiesDataTypes,
      targetServiceId,
    ],
  );

  const onFinishSelectMappingStatus = useCallback(
    (mappingStatuses: Item[]) => {
      const mappingStatusSelected = mappingStatuses.map(
        (mappingStatus: Item) => mappingStatus.value,
      );
      dispatchLocal(fetchPendingEntities, {
        params: {
          targetServiceId: +targetServiceId,
          dataTypes: selectedTargetService?.pendingEntitiesDataTypes as string[],
          mappingStatuses: mappingStatusSelected as string[],
          day,
        },
        stateId: StateIds.PendingEntityTable,
      });
      dispatch(
        setFilters({
          filterId: filterIds.pendingEntities,
          page: 0,
          rowsPerPage: 50,
          orderBy: 'id',
          orderDirection: 'asc',
        }),
      );
      setSelectedMappingStatuses(mappingStatusSelected as string[]);
    },
    [
      day,
      dispatch,
      dispatchLocal,
      selectedTargetService?.pendingEntitiesDataTypes,
      targetServiceId,
    ],
  );

  const filtersToShow = useMemo(
    () => ({
      sections: ['chipMultiSelect', 'datePicker'],
      filtersProps: {
        chipMultiSelect: {
          onClose: onFinishSelectMappingStatus,
          selectedItems: selectedMappingStatuses.map(
            (status) => allPendingMappingStatusHash[status],
          ),
          helperText: 'Mapping Status',
        },
        datePicker: {
          onDayChange: onDayChange,
          defaultDate: defaultStartDay,
        },
      },
    }),
    [onDayChange, onFinishSelectMappingStatus, selectedMappingStatuses],
  );

  const tableConfig = useMemo(() => {
    return getTableConfigs({ variant: undefined });
  }, []);

  const { TableComponent: PendingEntityTable } = useSimpleMuiTableBuilder({
    isLoading: isRequesting,
    filter,
    flatList: data,
    ...tableConfig,
    headerProps: {
      ...tableConfig.headerProps,
      filtersToShow,
    },
    _customRowProps: {
      onFinishSelectMappingStatus,
      onSingleEntityModalOpen,
      onPendingEntityStatusModalOpen,
    },
  });

  return (
    <Grid>
      <StandardPageHeader
        title="Pending Entities"
        subtitle="Review and manage pending integration entities"
        icon="Integration"
        sx={{ mb: 4 }}
      />

      <Grid>
        <PendingEntityTableContainer>
          <PendingEntityTable />
        </PendingEntityTableContainer>
      </Grid>

      <SingleEntityModal />
    </Grid>
  );
};

export default PendingEntity;
